interface ClipboardData {
  type: 'template-blocks';
  blocks: any[];
  timestamp: number;
  sourcePageId?: string;
}

class ClipboardManager {
  private static instance: ClipboardManager;
  private clipboardData: ClipboardData | null = null;

  private constructor() {}

  static getInstance(): ClipboardManager {
    if (!ClipboardManager.instance) {
      ClipboardManager.instance = new ClipboardManager();
    }
    return ClipboardManager.instance;
  }

  async copyBlocks(blocks: any[], sourcePageId?: string): Promise<boolean> {
    try {
      const clipboardData: ClipboardData = {
        type: 'template-blocks',
        blocks: blocks.map(block => ({
          ...block,
          id: undefined, // Remove ID so new ones are generated on paste
        })),
        timestamp: Date.now(),
        sourcePageId
      };

      // Store in memory for internal use
      this.clipboardData = clipboardData;

      // Try to use modern Clipboard API
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(JSON.stringify(clipboardData));
      } else {
        // Fallback to localStorage for older browsers
        localStorage.setItem('template-clipboard', JSON.stringify(clipboardData));
      }

      return true;
    } catch (error) {
      console.error('Failed to copy blocks:', error);
      
      // Fallback to localStorage
      try {
        const clipboardData: ClipboardData = {
          type: 'template-blocks',
          blocks: blocks.map(block => ({
            ...block,
            id: undefined,
          })),
          timestamp: Date.now(),
          sourcePageId
        };
        
        this.clipboardData = clipboardData;
        localStorage.setItem('template-clipboard', JSON.stringify(clipboardData));
        return true;
      } catch (fallbackError) {
        console.error('Fallback copy also failed:', fallbackError);
        return false;
      }
    }
  }

  async pasteBlocks(): Promise<any[] | null> {
    try {
      let clipboardData: ClipboardData | null = null;

      // Try to read from system clipboard first
      if (navigator.clipboard && navigator.clipboard.readText) {
        try {
          const text = await navigator.clipboard.readText();
          const parsed = JSON.parse(text);
          if (parsed.type === 'template-blocks') {
            clipboardData = parsed;
          }
        } catch (error) {
          // Not our data or parsing failed, try memory/localStorage
        }
      }

      // Fallback to memory
      if (!clipboardData && this.clipboardData) {
        clipboardData = this.clipboardData;
      }

      // Fallback to localStorage
      if (!clipboardData) {
        const stored = localStorage.getItem('template-clipboard');
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            if (parsed.type === 'template-blocks') {
              clipboardData = parsed;
            }
          } catch (error) {
            console.error('Failed to parse stored clipboard data:', error);
          }
        }
      }

      if (!clipboardData || !clipboardData.blocks) {
        return null;
      }

      // Check if data is not too old (24 hours)
      const maxAge = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
      if (Date.now() - clipboardData.timestamp > maxAge) {
        return null;
      }

      // Generate new IDs and offset positions for pasted blocks
      const pastedBlocks = clipboardData.blocks.map((block, index) => ({
        ...block,
        id: `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        position: {
          x: block.position.x + 20 + (index * 10), // Offset to avoid overlap
          y: block.position.y + 20 + (index * 10)
        }
      }));

      return pastedBlocks;
    } catch (error) {
      console.error('Failed to paste blocks:', error);
      return null;
    }
  }

  hasClipboardData(): boolean {
    // Check memory first
    if (this.clipboardData) {
      const maxAge = 24 * 60 * 60 * 1000;
      return Date.now() - this.clipboardData.timestamp <= maxAge;
    }

    // Check localStorage
    const stored = localStorage.getItem('template-clipboard');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed.type === 'template-blocks' && parsed.timestamp) {
          const maxAge = 24 * 60 * 60 * 1000;
          return Date.now() - parsed.timestamp <= maxAge;
        }
      } catch (error) {
        return false;
      }
    }

    return false;
  }

  clearClipboard(): void {
    this.clipboardData = null;
    localStorage.removeItem('template-clipboard');
  }

  getClipboardInfo(): { count: number; sourcePageId?: string } | null {
    if (this.clipboardData) {
      return {
        count: this.clipboardData.blocks.length,
        sourcePageId: this.clipboardData.sourcePageId
      };
    }

    const stored = localStorage.getItem('template-clipboard');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed.type === 'template-blocks') {
          return {
            count: parsed.blocks.length,
            sourcePageId: parsed.sourcePageId
          };
        }
      } catch (error) {
        return null;
      }
    }

    return null;
  }
}

export default ClipboardManager;
